
<template>
    <Section :key="allCompleted">
        <Alert type="warning" v-if="failCount > 0">
            <p>Data from {{ failCount }} {{ failCount > 1 ? 'Documents' : 'Document' }} couldn't be extracted out. <br/>
            This could be due to an image based PDF, or scanned PDF.</p>

            <p>Please ensure to check the highlighted resources and manually add the meta data that should be associated with the document, or the document may not be searchable.</p>
        </Alert>

        <FormulateForm v-if="showUploader">
            <FormulateInput
                v-model="page"
                label="Page:"
                :options="pageParentOptions"
                type="select"
                help="If the page has child pages, they will appear on selection"
            />

            <transition name="fade-in" mode="out-in" appear>
                <FormulateInput
                    v-if="childPageOptions"
                    v-model="childPage"
                    label="Child Page"
                    :options="childPageOptions"
                    type="select"
                />
            </transition>

            <transition name="fade-in" mode="out-in" appear>
                <div v-if="page !== 'none'" style="margin-bottom: 3em;">
                    <FormulateInput
                        v-model="pageCategory"
                        label="Tab"
                        type="text"
                        help="Case sensitive. This will be the 'Tab' the document sits within"
                    />

                    <FormulateInput
                        v-model="isPageOnly"
                        class="not-bold"
                        :options="{ isPageOnly: 'These resources are to be indexed to pages only, not Areas.' }"
                        type="customCheckbox"
                    />
                </div>
            </transition>

            <FormulateInput
                v-model="preUpload"
                ref="uploader"
                type="file"
                name="file"
                label=""
                validation="mime:application/pdf"
                multiple
                upload-behavior="delayed"
            />
            <Button v-if="preUpload" @click="startUpload">Upload {{ preUpload.files.length }} files</Button>
        </FormulateForm>

        <transition name="fade-in" mode="out-in">
            <article class="section--sm" v-if="showUploadMoreButton">
                <Button @click="resetUploads">Upload More Files</Button>
                <p class="footnote">Note: Ensure you have reviewed and made any changes you require prior to uploading more files, as the displayed list will be replaced by the newly uploaded files.</p>
            </article>
        </transition>

        <Section v-if="!showUploader">
            <UploadCard
                v-for="file in uploadingFiles"
                :key="`${file.name}-${randomNum}`"
                v-bind="file"
                :response="file"
                @removeResource="removeResource"
            />
        </Section>
    </Section>
</template>

<script>
import gsap from 'gsap'
import { mapState } from 'vuex'

export default {
    data() {
        return {
            page: 'none',
            childPage: 'none',
            pageCategory: null,
            isPageOnly: false,
            preUpload: null,
            showUploader: true,
            showUploadMoreButton: false,
            uploadingFiles: [],
            failCount: 0,
        }
    },

    computed: {
        ...mapState('App', ['pages']),

        pageParentOptions () {
            return this.pages
                .filter(({ hideFromNav }) => !hideFromNav)
                .filter(({ hide_from_nav }) => hide_from_nav === 'false')
                .reduce((acc, page) => {
                    return { ...acc, [page.path]: page.name }
                }, { 'none': 'No page - uploading for Core Skills' })
        },

        childPageOptions () {
            if (this.page !== 'none') {
                const selectedPage = this.pages.filter(({ path }) => path === this.page)[0]
                if (selectedPage && selectedPage.children && selectedPage.children.length > 0) {
                    return selectedPage.children.reduce((acc, page) => {
                        return { ...acc, [page.path]: page.name }
                    }, { 'none': 'None' })
                }
            }

            return false
        },

        randomNum () { return Math.random() },

        allCompleted () {
            const preUploadCount = this.preUpload && this.preUpload.files ? this.preUpload.files.length : 0
            const uploadedCount = this.uploadingFiles.filter(({ completed }) => completed).length

            // If the completed upload count is less than what's being uploaded, we want the tabs/pages
            // locked since editing is not complete yet
            if (this.preUpload && preUploadCount > uploadedCount) {
                if (this.showUploader) this.$emit('lockTabs')
            }

            // once complete, we need to give the user the option to add more files, and unlock the tab
            if (this.preUpload && preUploadCount === uploadedCount) {
                this.$emit('unlockTabs')
                this.toggleUploadMoreButton()
                return true
            }

            this.$emit('unlockTabs')
            return false
        }
    },

    methods: {
        async startUpload () {
            const { fileList } = this.preUpload

            // hides the drag and drop area and replaces with files that will be uploaded...
            this.showUploader = false
            this.showUploadMoreButton = false // failsafe to ensure this button is hidden

            // Create the 'uploading' objects prior to upload, we'll then reference
            // these objects in the upload results
            for (const { name } of fileList)
                this.uploadingFiles = await [...this.uploadingFiles, {
                    name, completed: false, percentage: 0
                }]

            fileList.forEach(file => this.uploadFile(file))
        },

        async uploadFile (file) {
            const { name } = file
            this.analyzingDocument = false
            // we will be referencing the uploaded files index in the upload list to update additional data,
            // ie "completed'"
            const uploadingFileIndex = this.uploadingFiles.findIndex(f => f.name === name)
            const fileData = this.uploadingFiles[uploadingFileIndex] // the actual file from the list

            // get the percentage upload values so we can animate the "progress bar"
            // and animate the percentage increments with gsap...
            const config = {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }, // file uploads require form-data...

                onUploadProgress: progressEvent => {
                    const percentCompleted = Math.floor((progressEvent.loaded * 100) / progressEvent.total)

                    gsap.to(fileData, {
                        duration: .4,
                        percentage: percentCompleted,
                        ease: 'power4.easeOut'
                    })
                }
            }

            // we need to append the file to formData to upload.
            let pageID
            let data = new FormData()
            data.append('file', file)

            console.log(this.page, this.childPage)

            const parent = this.page && this.page !== 'none'
                ? this.pages.filter(page => page.path === this.page)[0]
                : null

            const child = this.childPage && this.childPage !== 'none'
                ? parent.children.filter(page => page.path === this.childPage)[0]
                : null

            if (child) pageID = child.id
            else if (parent) pageID = parent.id

            // now we have the ID, we need to pass it with FormData
            if (pageID) data.append('page', pageID)
            if (this.page && this.pageCategory) data.append('pageCategory', this.pageCategory)
            if (parent && this.isPageOnly) data.append('isPageOnly', true)

            this.axios.post('/process-document', data, config)
                .then(res => {
                    console.log({ uploadResponse: res })
                    return res
                })
                .then(({ data }) => {
                    // if there is no title, it means there was a complete server / ai error
                    if (!data || data.status === 'fail' && !data.title) throw new Error("Sorry, this document couldn't be processed.")

                    data.type = 'document'
                    data.url = data.filepath

                    // if the document can't be processed, ie if it's a scanned document,
                    // the status will return 'fail', but still return the file data, ie name, filename, etc.
                    if (data.status === 'fail') {
                        this.failCount += 1
                        data.failedToProcess = true
                        data.tags = []
                        data.tags_nouns = []
                    }

                    // We initially set data to process as 'Process & Triage', this ensure that even,
                    // if the AI determins Process & Triage, we update it to what it should be.
                    if (!this.page) data.category = data.category.replace('Process & Triage', 'Presentation & Triage')

                    this.$set(fileData, 'data', data)
                })
                .catch(err => {
                    console.log(err.message || err)
                    this.$set(fileData, 'error', err.message || err)
                })
                .finally(() => {
                    this.$set(fileData, 'completed', true)
                })
        },

        // if the use has chosen to "Delete" the resource from the editing panel - it'll emit this event
        removeResource (resourceID) {
            this.uploadingFiles = this.uploadingFiles.filter(({ id }) => id !== resourceID)
        },

        toggleUploadMoreButton () { this.showUploadMoreButton = true },

        resetUploads () {
            // reset everything...
            this.failCount = 0
            this.preUpload = null
            this.showUploader = true
            this.showUploadMoreButton = false
            this.uploadingFiles = []
        }
    }
}
</script>
